import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';

import {
  DecisivePlayerPickerTaskAppearanceStatus,
  ManagerProgressionTaskState,
} from '@sorare/core/src/__generated__/globalTypes.ts';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons/index.tsx';
import { iconCheck } from '@sorare/core/src/atoms/icons/system/iconCheck.ts';
import { iconGift } from '@sorare/core/src/atoms/icons/system/iconGift.ts';
import { iconHourglass } from '@sorare/core/src/atoms/icons/system/iconHourglass.ts';
import { iconX } from '@sorare/core/src/atoms/icons/system/iconX.ts';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { HalfCircularProgressBar } from '@sorare/core/src/atoms/loader/CircularProgressIndicator/HalfCircularProgressBar/index.tsx';
import { LabelS } from '@sorare/core/src/atoms/typography/index.tsx';
import { LiveDot } from '@sorare/core/src/components/live/LiveDot/index.tsx';
import {
  DialogAdditionalParamKey,
  DialogKey,
} from '@sorare/core/src/components/navigation/WithDialogs/index.tsx';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft/index.tsx';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog.ts';

import { DecisivePlayerPickerTaskClaimStory } from '#src/components/missions/DailyTask/DecisivePlayerPickerTaskClaimStory/index.tsx';
import { DecisivePlayerPickerDialog } from '#src/components/missions/picker/DecisivePlayerPickerDialog/index.tsx';

import { IconWrapper, MissionButton } from '../ui.tsx';
import type { DailyPickerTaskWidget_decisivePlayerPickerTask } from './__generated__/index.graphql.ts';

const Img = styled.img`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  object-fit: contain;
`;
const PlayerImg = styled.img`
  width: 70%;
  height: 70%;
  object-fit: contain;
`;
const BottomPill = styled(Horizontal).attrs({ gap: 0.5 })`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 50%);
  border-radius: var(--double-unit);
  padding: 2px var(--unit);
  background-color: var(--c-nd-100-opaque);
`;
const GiftPill = styled(BottomPill)`
  background-color: var(--c-blue-400);
`;
const FailurePill = styled(BottomPill)`
  background-color: var(--c-rewards-orange);
  color: var(--c-black);
`;

type Props = {
  task: DailyPickerTaskWidget_decisivePlayerPickerTask;
  refetch: () => void;
};
export const DailyPickerTaskWidget = ({ task, refetch }: Props) => {
  const { templateIconUrl, id, aasmState, taskAppearances } = task;
  const [showPickerDialog, setShowPickerDialog] = useState(false);
  const [showClaimTaskStory, setShowClaimTaskStory] = useState(false);
  const taskAppearance = taskAppearances.at(0);
  const openDialog = useOpenDialog();

  if (aasmState === ManagerProgressionTaskState.READY) {
    if (!taskAppearance) {
      return (
        <>
          <MissionButton
            onClick={() => {
              setShowPickerDialog(true);
            }}
          >
            <HalfCircularProgressBar
              size={6}
              progresses={[1]}
              colors={[{ from: 'var(--c-nd-50)', to: 'var(--c-nd-50)' }]}
              backgroundColor="#021D04"
            />
            <IconWrapper>
              <Img src={templateIconUrl} alt="" />
            </IconWrapper>
          </MissionButton>
          {!!showPickerDialog && (
            <DecisivePlayerPickerDialog
              taskId={id}
              defaultSelectedSlotIndex={0}
              skipOnboarding
              onClose={() => setShowPickerDialog(false)}
              onSuccess={() => setShowPickerDialog(false)}
            />
          )}
        </>
      );
    }

    const { game, player } = taskAppearance;

    if (!taskAppearance.locked) {
      return (
        <>
          <MissionButton
            onClick={() => {
              setShowPickerDialog(true);
            }}
          >
            <HalfCircularProgressBar
              size={6}
              progresses={[1]}
              colors={[{ from: 'var(--c-nd-50)', to: 'var(--c-nd-50)' }]}
              backgroundColor="#021D04"
            />
            <IconWrapper>
              <PlayerImg
                src={player.squaredPictureUrl}
                alt={player.displayName}
              />
            </IconWrapper>
            {!!game?.date && (
              <BottomPill>
                <LabelS color="var(--c-nd-600)">
                  <FontAwesomeIcon icon={iconHourglass} />
                </LabelS>
                <LabelS bold>
                  <TimeLeft time={game.date} onEnded={refetch} />
                </LabelS>
              </BottomPill>
            )}
          </MissionButton>
          {!!showPickerDialog && (
            <DecisivePlayerPickerDialog
              taskId={id}
              defaultSelectedSlotIndex={0}
              skipOnboarding
              onClose={() => setShowPickerDialog(false)}
              onSuccess={() => setShowPickerDialog(false)}
            />
          )}
        </>
      );
    }

    if (
      taskAppearance.status === DecisivePlayerPickerTaskAppearanceStatus.FAILURE
    ) {
      return (
        <MissionButton
          onClick={() => {
            if (game?.id) {
              openDialog(DialogKey.game, game.id, {
                additionalParams: {
                  [DialogAdditionalParamKey.playerSlugs]:
                    taskAppearance.player.slug,
                },
              });
            }
          }}
        >
          <HalfCircularProgressBar
            size={6}
            progresses={[1]}
            colors={[
              {
                from: 'var(--c-rewards-orange)',
                to: 'var(--c-rewards-orange)',
              },
            ]}
            backgroundColor="#1D0202"
          />
          <IconWrapper>
            <PlayerImg
              src={player.squaredPictureUrl}
              alt={player.displayName}
            />
          </IconWrapper>
          <FailurePill>
            <LabelS>
              <FontAwesomeIcon icon={iconX} />
            </LabelS>
          </FailurePill>
        </MissionButton>
      );
    }

    return (
      <MissionButton
        onClick={() => {
          if (game?.id) {
            openDialog(DialogKey.game, game.id, {
              additionalParams: {
                [DialogAdditionalParamKey.playerSlugs]:
                  taskAppearance.player.slug,
              },
            });
          }
        }}
      >
        <HalfCircularProgressBar
          size={6}
          progresses={[1]}
          colors={[{ from: 'var(--c-nd-50)', to: 'var(--c-nd-50)' }]}
          backgroundColor="#1D0202"
        />
        <IconWrapper>
          <PlayerImg src={player.squaredPictureUrl} alt={player.displayName} />
        </IconWrapper>
        <BottomPill>
          <LiveDot animate />
          <LabelS color="var(--c-nd-600)">
            <FontAwesomeIcon icon={iconHourglass} />
          </LabelS>
        </BottomPill>
      </MissionButton>
    );
  }
  if (
    aasmState === ManagerProgressionTaskState.COMPLETED ||
    showClaimTaskStory
  ) {
    const { player } = taskAppearance || {};

    return (
      <>
        <MissionButton
          onClick={() => {
            setShowClaimTaskStory(true);
          }}
        >
          <HalfCircularProgressBar
            size={6}
            progresses={[1]}
            colors={[{ from: 'var(--c-green-400)', to: 'var(--c-green-400)' }]}
            backgroundColor="#021D042"
          />
          {!!player && (
            <IconWrapper>
              <PlayerImg
                src={player.squaredPictureUrl}
                alt={player.displayName}
              />
            </IconWrapper>
          )}
          <GiftPill>
            <LabelS>
              <FontAwesomeIcon icon={iconGift} />
            </LabelS>
          </GiftPill>
        </MissionButton>
        {!!showClaimTaskStory && (
          <DecisivePlayerPickerTaskClaimStory
            task={task}
            onClose={() => {
              setShowClaimTaskStory(false);
            }}
          />
        )}
      </>
    );
  }

  if (aasmState === ManagerProgressionTaskState.CLAIMED) {
    const { game, player } = taskAppearance || {};
    return (
      <MissionButton
        onClick={() => {
          if (game?.id && player?.slug) {
            openDialog(DialogKey.game, game.id, {
              additionalParams: {
                [DialogAdditionalParamKey.playerSlugs]: player.slug,
              },
            });
          }
        }}
      >
        <HalfCircularProgressBar
          size={6}
          progresses={[1]}
          colors={[{ from: 'var(--c-green-400)', to: 'var(--c-green-400)' }]}
          backgroundColor="#021D042"
        />
        {!!player && (
          <IconWrapper>
            <PlayerImg
              src={player.squaredPictureUrl}
              alt={player.displayName}
            />
          </IconWrapper>
        )}
        <BottomPill>
          <LabelS>
            <FontAwesomeIcon icon={iconCheck} />
          </LabelS>
        </BottomPill>
      </MissionButton>
    );
  }

  return null;
};

DailyPickerTaskWidget.fragments = {
  decisivePlayerPickerTask: gql`
    fragment DailyPickerTaskWidget_decisivePlayerPickerTask on DecisivePlayerPickerTask {
      id
      templateIconUrl
      aasmState
      taskAppearances {
        id
        locked
        status
        player {
          slug
          squaredPictureUrl
          displayName
        }
        game {
          id
          date
        }
      }
      ...DecisivePlayerPickerTaskClaimStory_decisivePlayerPickerTask
    }
    ${DecisivePlayerPickerTaskClaimStory.fragments.taskInterface}
  ` as TypedDocumentNode<DailyPickerTaskWidget_decisivePlayerPickerTask>,
};
